import { computed, ref } from 'vue';
import {
  useDraggable,
  useWindowSize,
  watchDebounced,
  TransitionPresets,
  executeTransition,
} from '@vueuse/core';
import { useGlobal } from '@/composables';
import { clamp } from '@/lib/utils';
import type { Area } from '@/types';

export function useMapSingleton() {
  const externalContainer = ref<HTMLDivElement | null>(null);

  const IMAGE_SIZE = { width: 9000 / 2, height: 6000 / 2 } as const;
  const imageWidth = computed(() => IMAGE_SIZE.width * _state.value.scale);
  const imageHeight = computed(() => IMAGE_SIZE.height * _state.value.scale);
  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const isRgb = ref(true);

  const fullscreenScale = ref(0);
  const state = ref({
    position: {
      x: 0,
      y: 0,
    },
    scale: 0,
  });
  const _state = ref({ ...state.value });
  const savedState = ref<typeof state.value | null>(null);

  const mapTransitionState = ref([
    state.value.position.x,
    state.value.position.y,
    state.value.scale,
  ]);

  const isFocusedAreaTooCloseToRightEdge = ref(false);

  const { isPoiFocused, isDragging } = useGlobal();

  const init = (ec: HTMLDivElement | null) => {
    externalContainer.value = ec;
  };

  const isDragDisabled = computed(() => isPoiFocused.value);
  useDraggable(externalContainer, {
    initialValue: {
      x: 0,
      y: 0,
    },
    onMove({ x, y }) {
      apply(
        () => {
          updatePositionToFitBounds(x, y);
        },
        {
          withTransition: false,
          saveState: false,
        },
      );
    },
    onStart() {
      isDragging.value = true;
    },
    onEnd() {
      isDragging.value = false;
      updateState();
    },
    disabled: isDragDisabled,
  });

  const updatePositionToFitBounds = (
    x: number = _state.value.position.x,
    y: number = _state.value.position.y,
  ) => {
    _state.value.position.x = clamp(-imageWidth.value + windowWidth.value, x, 0);
    _state.value.position.y = clamp(-imageHeight.value + windowHeight.value, y, 0);
  };

  const zoomTo = (scalingFactor: number) => {
    const { value: windowWidthValue } = windowWidth;
    const { value: windowHeightValue } = windowHeight;
    const halfWindowWidth = windowWidthValue / 2;
    const halfWindowHeight = windowHeightValue / 2;

    const windowStateMappedToOriginalImageDimensions = {
      x: -(_state.value.position.x - halfWindowWidth) / _state.value.scale,
      y: -(_state.value.position.y - halfWindowHeight) / _state.value.scale,
      width: windowWidthValue / _state.value.scale,
      height: windowHeightValue / _state.value.scale,
    };

    const target = {
      left:
        windowStateMappedToOriginalImageDimensions.x -
        windowStateMappedToOriginalImageDimensions.width * (scalingFactor / 2),
      top:
        windowStateMappedToOriginalImageDimensions.y -
        windowStateMappedToOriginalImageDimensions.height * (scalingFactor / 2),
      width: windowStateMappedToOriginalImageDimensions.width * scalingFactor,
      height: windowStateMappedToOriginalImageDimensions.height * scalingFactor,
    };

    const scale = Math.min(windowWidthValue / target.width, windowHeightValue / target.height);
    _state.value.scale = clamp(fullscreenScale.value, scale, 1);

    const offsetX = halfWindowWidth - (target.width * _state.value.scale) / 2;
    const offsetY = halfWindowHeight - (target.height * _state.value.scale) / 2;
    _state.value.position = {
      x: -(target.left * _state.value.scale - offsetX),
      y: -(target.top * _state.value.scale - offsetY),
    };
  };
  const zoomIn = (steps: number = 1) => {
    zoomTo(Math.pow(5 / 6, steps));
  };
  const zoomOut = (steps: number = 1) => {
    zoomTo(Math.pow(6 / 5, steps));
  };

  const isTransformationBeingApplied = ref(false);
  const apply = async (
    callback?: () => void,
    {
      saveState = false,
      restoreState = false,
      withTransition = true,
      updateState: shouldUpdateState = true,
    } = {},
  ) => {
    if (restoreState && savedState.value) _state.value = { ...savedState.value };
    if (saveState) savedState.value = { ...state.value };
    if (callback) callback();
    updatePositionToFitBounds();

    const from = [state.value.position.x, state.value.position.y, state.value.scale];
    const to = [_state.value.position.x, _state.value.position.y, _state.value.scale];

    const transition = async () => {
      isTransformationBeingApplied.value = true;
      await executeTransition(mapTransitionState, from, to, {
        duration: 500,
        transition: TransitionPresets.easeInOutCubic,
      });
      isTransformationBeingApplied.value = false;
    };

    withTransition ? await transition() : (mapTransitionState.value = to);
    if (shouldUpdateState) updateState();
  };

  const updateState = () => {
    state.value = { ..._state.value };
  };

  const focusOnArea = (area: Area) => {
    const halfWindowWidth = windowWidth.value / 2;
    const halfWindowHeight = windowHeight.value / 2;

    const scale = Math.min(halfWindowWidth / area.width, windowHeight.value / area.height);
    _state.value.scale = clamp(fullscreenScale.value, scale, 1);

    isFocusedAreaTooCloseToRightEdge.value =
      area.left * _state.value.scale + windowWidth.value >= imageWidth.value;

    const offsetX = isFocusedAreaTooCloseToRightEdge.value
      ? halfWindowWidth + (halfWindowWidth - area.width * _state.value.scale) / 2
      : (halfWindowWidth - area.width * _state.value.scale) / 2;
    const offsetY = halfWindowHeight - (area.height * _state.value.scale) / 2;
    _state.value.position = {
      x: -(area.left * _state.value.scale - offsetX),
      y: -(area.top * _state.value.scale - offsetY),
    };
  };

  watchDebounced(
    [windowWidth, windowHeight],
    ([newWidth, newHeight]) => {
      apply(
        () => {
          fullscreenScale.value = Math.max(
            Math.min(newWidth / IMAGE_SIZE.width, 1),
            Math.min(newHeight / IMAGE_SIZE.height, 1),
          );
          /*
            Current behavior => On window resize during loading screen, map scale clamps to value lower than max(width, height):
              the masked map image doesn't align perfectly with the real map underneath

            If clamping is disabled in favor of applying fullscreenScale.value
              on window resize when zoomed on POI fullscreen.value is applied and causes maximum zoom out
          */
          _state.value.scale = clamp(fullscreenScale.value, _state.value.scale, 1);
        },
        {
          withTransition: false,
        },
      );
    },
    { immediate: true, debounce: 500 },
  );

  const isHighQuality = computed(() => state.value.scale >= 0.6);

  return {
    isRgb,
    zoomIn,
    zoomOut,
    zoomTo,
    IMAGE_SIZE,
    init,
    focusOnArea,
    isDragDisabled,
    isFocusedAreaTooCloseToRightEdge,
    mapTransitionState,
    apply,
    isHighQuality,
    isTransformationBeingApplied,
  };
}

const cached = useMapSingleton();
export const useMap = () => cached;
